import type { PreviewSocketDataMessage } from "@innoclouds/ui";
import { backendLink, nextPreviewId, pathOf, socketTarget } from "./transport";

const sockets = new Map<number, PreviewSocket>();

class PreviewSocket extends EventTarget {
	static readonly CONNECTING = 0;
	static readonly OPEN = 1;
	static readonly CLOSING = 2;
	static readonly CLOSED = 3;

	readonly CONNECTING = 0;
	readonly OPEN = 1;
	readonly CLOSING = 2;
	readonly CLOSED = 3;

	readonly id = nextPreviewId();
	readonly url: string;
	readyState = 0;
	binaryType: BinaryType = "blob";
	bufferedAmount = 0;
	extensions = "";
	protocol = "";

	onopen: ((event: Event) => void) | null = null;
	onmessage: ((event: MessageEvent) => void) | null = null;
	onerror: ((event: Event) => void) | null = null;
	onclose: ((event: CloseEvent) => void) | null = null;

	constructor(url: string | URL, connect: boolean) {
		super();
		this.url = String(url);
		if (!connect) {
			window.setTimeout(() => this.refuse());
			return;
		}
		sockets.set(this.id, this);
		backendLink().socket(this.id, "open", pathOf(this.url));
		window.setTimeout(() => {
			if (this.readyState !== this.CONNECTING) return;
			this.readyState = this.OPEN;
			this.fire(new Event("open"));
		});
	}

	send(data: string) {
		if (this.readyState === this.OPEN) backendLink().socket(this.id, "send", undefined, String(data));
	}

	close() {
		if (this.readyState >= this.CLOSING) return;
		this.readyState = this.CLOSED;
		sockets.delete(this.id);
		backendLink().socket(this.id, "close");
		this.fire(new CloseEvent("close", { code: 1000, wasClean: true }));
	}

	receive(data: string) {
		if (this.readyState === this.OPEN) this.fire(new MessageEvent("message", { data }));
	}

	private refuse() {
		this.readyState = this.CLOSED;
		this.fire(new Event("error"));
		this.fire(new CloseEvent("close", { code: 1006, wasClean: false }));
	}

	private fire(event: Event) {
		this.dispatchEvent(event);
		const handler = this[`on${event.type}` as "onopen"] as ((event: Event) => void) | null;
		handler?.call(this, event);
	}
}

export const patchWebSocket = () => {
	window.WebSocket = new Proxy(window.WebSocket, {
		construct: (NativeWebSocket, [url, protocols]) => {
			const target = socketTarget(url);
			if (target === "native") return new NativeWebSocket(url, protocols);
			return new PreviewSocket(url, target === "mock" && backendLink().acceptsSockets);
		},
	});
};

export const receiveSocketData = ({ id, data }: Pick<PreviewSocketDataMessage, "id" | "data">) => sockets.get(id)?.receive(data);
